import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Play, Send } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { Badge, Button, PageState, Skeleton, StatusDot, cn, type BadgeVariant } from "@fox1ck-org/ui";
import type { AffiliateNetwork } from "@gen/tracker/v1/affiliate_network_pb";
import { ConversionStatus } from "@gen/tracker/v1/conversion_pb";

import { Aside, Panel } from "@/components/campaign/panel";
import { useCampaignNetworks, useLastPostback, useNetworkStatuses } from "@/components/campaign/partners";
import { ConversionStatusBadge } from "@/components/canonical/conversion-status-badge";
import { useT, type TKey } from "@/lib/i18n";
import { formatAge } from "@/lib/format";
import { formatStamp, postbackUrlOn } from "@/lib/utils";
import { errorText } from "@/lib/errors";
import { useReportParams } from "@/lib/hooks/use-report-params";
import { useRenderAdLink } from "@/lib/services/campaign-service";
import { RESULT_VIEW, resultKey, useListPostbacks, useUnmappedStatuses } from "@/lib/services/postback-service";
import { newNonce, readNonce, testLink, useSendTestPostback, useSourceEvents, useTestClick } from "@/lib/services/test-postback-service";
import { toastFailure } from "@/lib/plan";
import type { CardContext } from "./card";

/**
 * What a partner's ruling becomes in the source (decision 07): the tracker
 * sends nothing itself — the ruling takes the Keitaro road through
 * traffic-controller and vibe-dispatch, which makes these events of it.
 */
const SOURCE_EVENT: Partial<Record<ConversionStatus, string>> = {
  [ConversionStatus.HOLD]: "Lead",
  [ConversionStatus.APPROVED]: "Purchase",
  [ConversionStatus.REJECTED]: "LeadRejected",
  [ConversionStatus.TRASH]: "LeadTrash",
};

/**
 * Ссылки и постбеки (доска CLinks): the link for the source, rendered by the
 * server from the source's own template (RenderAdLink), with what each mark
 * carries; the partners' postback addresses, how their status words read and
 * a test that fires only on the buyer's OWN test click; what goes on to the
 * source; and the campaign's latest postbacks.
 */
export function LinksTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const [nonce, setNonce] = useState(() => readNonce(ctx.campaign.id));
  const test = useTestClick(ctx.campaign.id, nonce);
  const { networks, loading } = useCampaignNetworks(ctx.campaign, ctx.item);
  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto xl:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-3">
        <AdLinkPanel ctx={ctx} nonce={nonce} found={!!test.data?.found} clickedAt={test.data?.clickedAt} onTest={setNonce} />
        <SourcePanel ctx={ctx} />
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        {loading ? (
          <Skeleton className="h-60 rounded-lg" />
        ) : networks.length === 0 ? (
          <Panel title={t("links.partners")}>
            <p className="px-3.5 py-4 text-sm text-muted-foreground">{t("setup.postbacks.none")}</p>
          </Panel>
        ) : (
          networks.map((n) => <PartnerPanel key={n.id} ctx={ctx} network={n} nonce={test.data?.found ? nonce : ""} />)
        )}
        <RecentPostbacks ctx={ctx} />
      </div>
    </div>
  );
}

function CopyBox({ value }: { value: string }) {
  const t = useT();
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-surface-2 py-1 pl-3 pr-1">
      <code className="min-w-0 flex-1 truncate font-mono text-sm" title={value}>
        {value || "—"}
      </code>
      <Button variant="ghost" size="sm" disabled={!value} onClick={() => void navigator.clipboard.writeText(value).then(() => toast.success(t("setup.copied")))}>
        <Copy className="size-3.5" /> {t("setup.copy")}
      </Button>
    </div>
  );
}

function AdLinkPanel({ ctx, nonce, found, clickedAt, onTest }: { ctx: CardContext; nonce: string; found: boolean; clickedAt: string | undefined; onTest: (n: string) => void }) {
  const t = useT();
  const ad = useRenderAdLink(ctx.campaign.id);
  const link = ad.data?.link;
  const source = ctx.item?.trafficSourceName || t("links.source");
  // The value the ad account's «URL сайта» takes, else the bare address.
  const main = link?.values[0]?.value || link?.link || "";
  const serves = link?.domainServes ?? ctx.item?.domainServes;

  return (
    <Panel
      title={t("card.links.for", { source })}
      aside={
        link?.fqdn ? (
          <Badge variant={serves ? "ok" : "warn"} dot>
            {serves ? t("card.links.domainOk") : t("card.links.domainWait")}
          </Badge>
        ) : null
      }
    >
      <div className="flex flex-col gap-3 p-3.5">
        {ad.isLoading ? (
          <Skeleton className="h-32" />
        ) : ad.error ? (
          <PageState mode="error" title={t("links.failed")} error={errorText(ad.error)} retry={() => void ad.refetch()} retryLabel={t("lib.common.retry")} />
        ) : ad.data?.noSource ? (
          <p className="text-sm text-muted-foreground">
            {t("links.noSource")}{" "}
            <Link to={`/campaigns/${ctx.campaign.id}/edit#source`} className="text-primary hover:underline">
              {t("links.noSource.pick")}
            </Link>
          </p>
        ) : (
          <>
            {ad.data?.missing ? <p className="text-xs text-warn">{t("links.preview", { reason: ad.data.missing })}</p> : null}
            <CopyBox value={main} />
            {(link?.values ?? []).slice(1).map((v) => (
              <div key={v.key} className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">{v.adField ? t("links.adField", { field: v.adField }) : v.label}</span>
                <CopyBox value={v.value} />
              </div>
            ))}
            {!serves && link?.fqdn ? <p className="text-xs text-warn">{t("links.domainNotServing", { fqdn: link.fqdn })}</p> : null}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!link?.link}
                onClick={() => {
                  const n = newNonce(ctx.campaign.id);
                  onTest(n);
                  window.open(testLink(link!.link, n), "_blank", "noopener");
                }}
              >
                <Play className="size-3.5" /> {t("card.links.test")}
              </Button>
              <span className="text-xs text-muted-foreground">
                {found && clickedAt ? t("links.checked", { ago: formatAge((Date.now() - Date.parse(clickedAt)) / 1000) }) : nonce ? t("links.waiting") : t("card.links.test.hint")}
              </span>
            </div>
            {(link?.params ?? []).length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-1.5 pr-3 font-medium">{t("card.links.mark")}</th>
                    <th className="py-1.5 pr-3 font-medium">{t("card.links.fills", { source })}</th>
                    <th className="py-1.5 font-medium">{t("card.links.macro")}</th>
                  </tr>
                </thead>
                <tbody>
                  {link!.params.map((p) => (
                    <tr key={p.param} className="border-b border-border last:border-b-0">
                      <td className="py-1.5 pr-3">
                        <code className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-xs">{p.param}</code>
                      </td>
                      <td className="py-1.5 pr-3">
                        {p.label || "—"}
                        {p.param === link!.adIdParam ? <span className="ml-1.5 text-xs text-muted-foreground">{t("card.links.adId")}</span> : null}
                      </td>
                      <td className="py-1.5 font-mono text-xs text-muted-foreground">{p.macro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}

function PartnerPanel({ ctx, network, nonce }: { ctx: CardContext; network: AffiliateNetwork; nonce: string }) {
  const t = useT();
  const p = useReportParams();
  const url = postbackUrlOn(ctx.item?.fqdn, network.inboundPostbackUrl);
  const last = useLastPostback(network.id);
  const words = useNetworkStatuses(network.id);
  const unmapped = useUnmappedStatuses(p.from, p.to, p.tz);
  const send = useSendTestPostback();
  const at = last.data?.items[0]?.receivedAt;
  const gaps = (unmapped.data ?? []).filter((u) => u.networkId === network.id);
  const rows = (words.data ?? []).filter((w) => w.status !== ConversionStatus.UNSPECIFIED).slice(0, 8);

  return (
    <Panel title={t("card.links.postbackFor", { network: network.name })} aside={<Aside>{t("card.links.postback.aside")}</Aside>}>
      <div className="flex flex-col gap-3 p-3.5">
        <CopyBox value={url} />
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!nonce || send.isPending}
            title={nonce ? undefined : t("links.test.needClick")}
            onClick={() =>
              send.mutate(
                { campaignId: ctx.campaign.id, networkId: network.id, nonce },
                {
                  onSuccess: (r) => {
                    const key = resultKey(r.result);
                    toast.success(t("links.test.sent", { result: key ? t(RESULT_VIEW[key].label) : r.detail || "—" }));
                  },
                  onError: (e) => toastFailure(e, t("campaign.toast.failed")),
                },
              )
            }
          >
            <Send className="size-3.5" /> {t("links.test.send")}
          </Button>
          <span className="text-xs text-muted-foreground">
            {at ? t("card.links.lastReal", { ago: formatAge((Date.now() - Date.parse(at)) / 1000) }) : last.isLoading ? "" : t("setup.postbacks.never")}
          </span>
        </div>
        {words.isLoading ? (
          <Skeleton className="h-28" />
        ) : rows.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">{t("card.links.word")}</th>
                <th className="py-1.5 pr-3 font-medium">{t("card.links.ours")}</th>
                <th className="py-1.5 font-medium">{t("card.links.inSource", { source: ctx.item?.trafficSourceName || t("links.source") })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.word} className="border-b border-border last:border-b-0">
                  <td className="py-1.5 pr-3">
                    <code className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-xs">{w.word}</code>
                  </td>
                  <td className="py-1.5 pr-3">
                    <ConversionStatusBadge status={w.status} />
                  </td>
                  <td className="py-1.5 font-mono text-xs">{SOURCE_EVENT[w.status] ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {gaps.map((g) => (
          <Link key={`${g.networkId}:${g.rawStatus}`} to={`/journal/postbacks?network=${g.networkId}`} className="text-sm text-warn hover:underline">
            {t("links.unmapped", { status: g.rawStatus, network: g.networkName })}
          </Link>
        ))}
      </div>
    </Panel>
  );
}

const STATE_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = { delivered: "ok", waiting: "warn", dead: "danger", test: "neutral" };

/**
 * В источник (решение 07): the tracker sends nothing to a source itself. A
 * partner's ruling on a lead is forwarded as Keitaro's S2S postback into
 * traffic-controller, and vibe-dispatch makes the source's events of it —
 * what this panel lists, newest first, with their delivery state.
 */
function SourcePanel({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const p = useReportParams();
  const events = useSourceEvents(ctx.campaign.id);
  const items = events.data ?? [];
  const live = items.some((e) => e.mode === "live");
  const source = ctx.item?.trafficSourceName || t("links.source");
  const badge: { variant: BadgeVariant; label: TKey } | undefined = events.isLoading
    ? undefined
    : items.length === 0
      ? { variant: "neutral", label: "links.capi.idle" }
      : live
        ? { variant: "ok", label: "links.capi.live" }
        : { variant: "info", label: "links.capi.test" };
  return (
    <Panel title={t("card.links.toSource", { source })} aside={badge ? <Badge variant={badge.variant} dot>{t(badge.label)}</Badge> : null}>
      <div className="flex flex-col">
        {items.length === 0 ? (
          <p className="px-3.5 py-3 text-sm text-muted-foreground">{events.isLoading ? "…" : t("links.capi.none")}</p>
        ) : (
          <ul className="flex flex-col">
            {items.slice(0, 5).map((e) => (
              <li key={e.eventId} className="flex items-center gap-2.5 border-b border-border px-3.5 py-2 text-sm last:border-b-0">
                <StatusDot tone={STATE_TONE[e.state] ?? "neutral"} />
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatStamp(e.createdAt, p.tz, { clock: true })}</span>
                <code className="font-mono text-sm">{e.eventName}</code>
                {e.value ? <span className="font-mono text-xs text-muted-foreground">{`${e.value} ${e.currency}`}</span> : null}
                <span className={cn("ml-auto text-xs text-muted-foreground", e.state === "dead" && "text-destructive")} title={e.lastError}>
                  {t(`links.capi.state.${e.state as "delivered"}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="border-t border-border px-3.5 py-2 text-xs text-muted-foreground">{t("card.links.capi.road")}</p>
      </div>
    </Panel>
  );
}

function RecentPostbacks({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const p = useReportParams();
  const q = useListPostbacks({ from: p.from, to: p.to, timezone: p.tz, campaignId: ctx.campaign.id, limit: 8, offset: 0 }, { refetchInterval: 15_000 });
  const rows = q.data?.items ?? [];
  return (
    <Panel
      title={t("links.recent")}
      aside={
        <Button asChild variant="ghost" size="sm">
          <Link to={`/journal/postbacks?campaign=${ctx.campaign.id}`}>{t("card.links.journal")}</Link>
        </Button>
      }
    >
      {q.isLoading ? (
        <Skeleton className="m-3.5 h-28" />
      ) : rows.length === 0 ? (
        <p className="px-3.5 py-6 text-sm text-muted-foreground">{t("links.recent.none")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-3.5 py-2 font-medium">{t("links.recent.when")}</th>
              <th className="px-2 py-2 font-medium">{t("links.recent.from")}</th>
              <th className="px-2 py-2 font-medium">{t("links.recent.got")}</th>
              <th className="px-3.5 py-2 font-medium">{t("links.recent.did")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = resultKey(r.result);
              const view = key ? RESULT_VIEW[key] : undefined;
              return (
                <tr key={r.eventId} className="border-b border-border last:border-b-0">
                  <td className="px-3.5 py-2 font-mono text-xs tabular-nums">{formatStamp(r.receivedAt, p.tz, { clock: true })}</td>
                  <td className="px-2 py-2">{r.networkName || "—"}</td>
                  <td className="max-w-40 truncate px-2 py-2 font-mono text-xs text-muted-foreground">{r.rawStatus || r.rawQuery.slice(0, 32)}</td>
                  <td className="px-3.5 py-2">
                    {view ? (
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={view.tone ?? "neutral"} />
                        {t(view.label)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
